import { Browser } from "@capacitor/browser";
import { useMutation, useQuery } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import { groupBy } from "lodash";
import { useState } from "react";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { get, post } from "api";
import { Button } from "atoms/button/Button";
import { PastTreatmentCard } from "molecules/containers/past-treatment-card/PastTreatmentCard";
import { TreatmentCard } from "molecules/containers/treatment-card/TreatmentCard";
import { UserAndCash } from "molecules/subheaders/user-and-cash/UserAndCash";
import { ModalSelectorList } from "organisms/selection-modals/modal-selector-list/ModalSelectorList";
import {
  NoTreatmentsContainer,
  TreatmentList,
} from "organisms/treatment-list/TreatmentList";
import { queryClient } from "pages/Root";
import { IfFlag, useFeatureFlag } from "services/Flags";
import {
  getBookAppointmentAction,
  getProductTitle,
} from "services/HelperService";
import { waitObj } from "toolbox/Promise";
import {
  getBillingIntervalBase,
  parseBillingPeriodName,
} from "utils/billingPeriod.helpers";

const load = async () => {
  const userInfo = queryClient.fetchQuery(["/user"], () => get("/user"), {
    staleTime: 0,
  });

  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const myTreatments = queryClient.fetchQuery(["/platform-user-packages"], () =>
    get("/platform-user-packages")
  );

  const memberships = queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );

  const locations = queryClient.fetchQuery(["/platform/locations"], () =>
    get("/platform/locations", {})
  );

  const platformLocation = queryClient.fetchQuery(["/platform-location"], () =>
    get("/platform-location")
  );

  const checkInAvailable = queryClient.fetchQuery(["/user/check-in"], () =>
    get("/user/check-in")
  );

  const authClaims = queryClient.fetchQuery(["/auth/claims"], () =>
    get("/auth/claims")
  );

  return waitObj({
    platform,
    myTreatments,
    memberships,
    userInfo,
    locations,
    platformLocation,
    checkInAvailable,
    authClaims,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletTreatmentsLoader: LoaderFunction = async () => {
  const data = await load();
  if (
    !data.platform.platformSettings?.marketplaceEnabled &&
    !data.myTreatments.length &&
    !data.memberships.length
  ) {
    return redirect("/wallet");
  }
  return data;
};

const WalletTreatmentsPage = () => {
  const navigate = useNavigate();
  const loaderData = useLoaderData() as LoaderData;

  // Use query for reactive updates to treatments
  const { data: myTreatments = loaderData.myTreatments } = useQuery({
    queryKey: ["/platform-user-packages"],
    queryFn: () => get("/platform-user-packages"),
    initialData: loaderData.myTreatments,
  });

  const {
    userInfo,
    platform,
    memberships,
    locations,
    checkInAvailable,
    authClaims,
  } = loaderData;
  const isUnitedDerm = useFeatureFlag("unitedDerm");
  const isSelectPatientLocationActive = useFeatureFlag("selectPatientLocation");
  const isV3Enabled = true;

  // Confetti from card position
  const triggerConfettiFromCard = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    const colors = [
      "#D4AF37",
      "#CFB53B",
      "#F0E68C",
      "#DAA520",
      "#C9B037",
      "#B8860B",
      "#F4E4C1",
      "#E6C794",
    ];

    const defaults = {
      origin: { x, y },
      colors,
      ticks: 200,
      gravity: 1,
      drift: 0,
      startVelocity: 35,
      shapes: ["circle", "square"] as confetti.Shape[],
      scalar: 1,
    };

    const particleCount = 50;
    confetti({
      ...defaults,
      particleCount,
      spread: 70,
    });

    confetti({
      ...defaults,
      particleCount: particleCount / 2,
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
    });
  };

  // Check if user can skip scanning (admin or checked in)
  const isAdminImpersonation = !!authClaims?.claims?.adminUserId;
  const skipScanInOffice = !checkInAvailable || isAdminImpersonation;

  // Location state
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState("");

  // Redeem state for v3
  const [treatmentShowingQuantity, setTreatmentShowingQuantity] = useState<
    string | null
  >(null);
  const [treatmentShowingSelector, setTreatmentShowingSelector] = useState<
    string | null
  >(null);
  const [selectedTreatmentId, setSelectedTreatmentId] = useState<string | null>(
    null
  );

  // Persist redeemed today in localStorage with local date key
  const getStorageKey = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `redeemed-today-${year}-${month}-${day}`;
  };

  // Clean up old localStorage keys on mount
  const cleanupOldStorageKeys = () => {
    const currentKey = getStorageKey();
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("redeemed-today-") && key !== currentKey) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  };

  const [redeemedToday, setRedeemedToday] = useState<Record<string, number>>(
    () => {
      cleanupOldStorageKeys();
      try {
        const stored = localStorage.getItem(getStorageKey());
        return stored ? JSON.parse(stored) : {};
      } catch {
        return {};
      }
    }
  );

  // Redeem mutation for direct redemption
  const redeemMutation = useMutation({
    mutationFn: ({
      packageId,
      locationId,
      isFromMembership,
      productId,
      quantity,
    }: {
      packageId: string;
      locationId?: string;
      isFromMembership: boolean;
      productId: string;
      quantity: number;
    }) =>
      post("/platform-user-package-items/redeem-in-app", {
        packageId,
        locationId,
        isFromMembership,
        productId,
        quantity,
      }),
    onSuccess: async (_, variables) => {
      // Track redeemed items - confetti will trigger from card via useEffect
      setRedeemedToday((prev) => {
        const updated = {
          ...prev,
          [variables.packageId]:
            (prev[variables.packageId] || 0) + variables.quantity,
        };
        // Persist to localStorage
        try {
          localStorage.setItem(getStorageKey(), JSON.stringify(updated));
        } catch (error) {
          // Silently fail
        }
        return updated;
      });

      // Force refetch to get updated data
      await queryClient.invalidateQueries({
        queryKey: ["/platform-user-packages"],
      });
      await queryClient.refetchQueries({
        queryKey: ["/platform-user-packages"],
        type: "active",
      });

      setTreatmentShowingQuantity(null);
      setTreatmentShowingSelector(null);
      setSelectedTreatmentId(null);
    },
    onError: (error: any) => {
      // Silently handle error and reset state
      setTreatmentShowingQuantity(null);
      setTreatmentShowingSelector(null);
      setSelectedTreatmentId(null);
    },
  });

  const handleRedeemButtonClick = (treatment: (typeof myTreatments)[0]) => {
    // Check if multiple items in package
    if (treatment.platformUserPackageItemModels.length > 1) {
      // Show treatment selector first
      setTreatmentShowingSelector(treatment.packageId);
      setSelectedTreatmentId(null);
    } else {
      // Single treatment - check quantity
      const itemModel = treatment.platformUserPackageItemModels[0];
      if (itemModel && itemModel.remainingQuantity > 1) {
        setTreatmentShowingQuantity(treatment.packageId);
        setSelectedTreatmentId(itemModel.productId);
      } else {
        // Just redeem 1
        handleRedeem(treatment, 1, itemModel?.productId);
      }
    }
  };

  const handleTreatmentSelected = (
    treatment: (typeof myTreatments)[0],
    selections: Record<string, number>
  ) => {
    // Now we handle multi-select redemption
    // selections is a map of productId -> quantity
    setTreatmentShowingSelector(null);

    const isMemberBenefit = treatment.type === "MembershipTreatment";

    // Process each selection
    Object.entries(selections).forEach(([productId, quantity]) => {
      if (quantity > 0) {
        handleRedeem(treatment, quantity, productId);
      }
    });
  };

  const handleRedeem = (
    treatment: (typeof myTreatments)[0],
    quantity: number,
    productId?: string
  ) => {
    const isMemberBenefit = treatment.type === "MembershipTreatment";
    const product =
      productId || treatment.platformUserPackageItemModels[0]?.productId;

    if (!product) {
      return;
    }

    if (skipScanInOffice) {
      // Can redeem directly
      // For membership treatments, use the treatment's locationId
      // For purchased packages, don't send locationId (let backend determine it)
      redeemMutation.mutate({
        packageId: treatment.packageId,
        locationId: isMemberBenefit
          ? treatment.locationId ?? undefined
          : undefined,
        isFromMembership: isMemberBenefit,
        productId: product,
        quantity,
      });
    } else {
      // Navigate to scan page
      navigate("/scan-redeem-treatment-in-location", {
        state: {
          packageId: treatment.packageId,
          isFromMembership: isMemberBenefit.toString(),
          productId: product,
          remainingAmount: treatment.remainingTreatmentAmount - quantity,
          quantity,
          locationId: treatment.locationId,
        },
      });
    }
  };

  const availableLocationIds = myTreatments
    ?.map((t) => t.locationId)
    .filter((id): id is string => typeof id === "string");

  const filterPlatformLocationsBasedOnAvailableTreatments = (
    availableLocationIds?: string[] | null,
    platformLocations?: typeof locations.items
  ) => {
    return (
      (availableLocationIds?.length
        ? platformLocations?.filter(({ id }) =>
            availableLocationIds.includes(id)
          )
        : platformLocations) ?? []
    );
  };

  const filteredLocations = filterPlatformLocationsBasedOnAvailableTreatments(
    availableLocationIds,
    locations.items
  );

  // Handles legacy locations that may not have url, email, or phone number
  const bookablePlatformLocations = filteredLocations.filter((location) =>
    getBookAppointmentAction(
      location.bookAppointmentUrl,
      location.phoneNumber,
      location.email
    )
  );
  const locationMap = new Map(
    filteredLocations?.map((location) => [location.id, location])
  );

  const handleBookAppointmentClick = () => {
    if (bookablePlatformLocations.length === 1) {
      const [location] = bookablePlatformLocations;
      const href = getBookAppointmentAction(
        location.bookAppointmentUrl,
        location.phoneNumber,
        location.email
      );
      if (href) {
        Browser.open({ url: href });
      }
    } else if (bookablePlatformLocations?.length > 1) {
      setSelectedLocationId(""); // Reset selection
      setIsLocationModalOpen(true);
    }
    // If there are 0 valid locations, do nothing (button won't be shown anyway)
  };

  const memberBenefits = myTreatments.filter(
    ({ type }) => type === "MembershipTreatment"
  );

  const treatments = myTreatments.filter(
    ({ type }) => type === "Treatment" || type === "Package"
  );

  // Count active treatments (those with remaining treatments)
  const activeTreatmentsCount = myTreatments.filter(
    (treatment) => treatment.remainingTreatmentAmount > 0
  ).length;

  const getBonusText = (membershipRewardGroupIds?: string[] | null) => {
    if (!membershipRewardGroupIds) {
      return null;
    }

    const treatmentTypes = memberships
      .flatMap(({ membership }) => membership.membershipRewardGroups)
      .filter(
        (rg) =>
          rg &&
          membershipRewardGroupIds?.includes(rg.id) &&
          !rg.beautyBank?.creditAmount
      )
      .map((rg) => {
        if (rg && rg.billingPeriodInterval !== 0) {
          return "Member Benefit";
        } else if (rg && rg.billingPeriodsBeforeFirstReward === 0) {
          return "Sign-up Bonus";
        } else {
          // Find the membership that contains this reward group to get the billing period interval
          const membership = memberships.find(({ membership }) =>
            membership.membershipRewardGroups?.some((mrg) => mrg.id === rg?.id)
          );

          const billingPeriodInterval =
            membership?.membership.billingPeriodInterval ?? 1;
          const parsedBillingPeriodInterval = parseBillingPeriodName(
            billingPeriodInterval
          );
          const billingIntervalBase = getBillingIntervalBase(
            parsedBillingPeriodInterval
          );

          return `${
            (rg?.billingPeriodsBeforeFirstReward ?? 1) *
            (membership?.membership.billingPeriodIntervalCount ?? 1)
          }-${
            billingIntervalBase.charAt(0).toUpperCase() +
            billingIntervalBase.slice(1)
          } Bonus`;
        }
      });

    const treatmentTypesSet = new Set(treatmentTypes);

    if (
      (treatmentTypesSet.size === 1 &&
        treatmentTypesSet.has("Member Benefit")) ||
      treatmentTypesSet.size > 1
    ) {
      return null;
    }
    return treatmentTypesSet.values().next().value;
  };

  const locationsIdToNameMap = new Map(
    filteredLocations?.map(({ id, name }) => [id, name])
  );
  const treatmentsByLocationId = groupBy(treatments, "locationId");
  const treatmentsWithUnknownLocation = treatmentsByLocationId[`${null}`];

  // Helper to get membership name from reward group IDs
  const getMembershipName = (
    membershipRewardGroupIds?: string[] | null
  ): string | null => {
    if (!membershipRewardGroupIds || membershipRewardGroupIds.length === 0) {
      return null;
    }

    const membership = memberships.find(({ membership }) =>
      membership.membershipRewardGroups?.some((rg) =>
        membershipRewardGroupIds.includes(rg.id)
      )
    );

    return membership?.membership.name ?? null;
  };

  // Separate active and past treatments for v3, sort membership treatments to top
  const activeTreatments = myTreatments
    .filter((treatment) => {
      // For membership treatments: must be active AND have remaining treatments
      if (treatment.type === "MembershipTreatment") {
        return treatment.active && treatment.remainingTreatmentAmount > 0;
      }
      // For regular purchased treatments: just needs remaining treatments
      // (the 'active' field may not be reliable for purchased packages)
      return treatment.remainingTreatmentAmount > 0;
    })
    .sort((a, b) => {
      // Sort membership treatments first
      const aIsMember = a.type === "MembershipTreatment" ? 0 : 1;
      const bIsMember = b.type === "MembershipTreatment" ? 0 : 1;
      return aIsMember - bIsMember;
    });

  const pastTreatments = myTreatments
    .filter((treatment) => {
      // For membership treatments: show as past if inactive (cancelled/expired)
      if (treatment.type === "MembershipTreatment") {
        return !treatment.active;
      }
      // For regular purchased treatments: show as past if fully redeemed
      return treatment.remainingTreatmentAmount === 0;
    })
    .sort((a, b) => {
      // Sort membership treatments first
      const aIsMember = a.type === "MembershipTreatment" ? 0 : 1;
      const bIsMember = b.type === "MembershipTreatment" ? 0 : 1;
      return aIsMember - bIsMember;
    });

  // Group treatments by location for v3
  const activeTreatmentsByLocation = groupBy(activeTreatments, "locationId");
  const pastTreatmentsByLocation = groupBy(pastTreatments, "locationId");

  const isPartnerEmrBookingEnabled =
    platform.platformSettings?.partnerEmrBookingEnabled;

  return (
    <div className="flex grow flex-col overflow-auto">
      {!isV3Enabled && (
        <UserAndCash
          firstName={userInfo.firstName}
          lastName={userInfo.lastName}
          repeatCashBalance={userInfo.repeatCashBalance}
          hideRepeatCash={!platform.platformSettings?.repeatCashEnabled}
        />
      )}

      {isV3Enabled ? (
        <>
          {/* V3 Layout - Credit card style for active, smaller cards for past */}
          {myTreatments.length === 0 && (
            <div className="mx-4 mt-4 overflow-clip rounded-lg">
              <NoTreatmentsContainer text="You haven't purchased any treatments yet. Browse the shop to buy your first treatment and start your journey!" />
            </div>
          )}

          {/* Active Treatments - Grouped by Location */}
          {activeTreatments.length > 0 && (
            <>
              {Object.entries(activeTreatmentsByLocation).map(
                ([locationId, locationTreatments]) => {
                  const locationName =
                    locationId !== "null"
                      ? locationsIdToNameMap.get(locationId)
                      : null;

                  return (
                    <div key={locationId} className="pb-6 pt-4">
                      {locationName && (
                        <div className="mb-4 px-4">
                          <span className="text-body3 font-medium text-secondary">
                            {locationName}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col gap-5 px-4">
                        {locationTreatments.map((treatment) => {
                          const isMemberBenefit =
                            treatment.type === "MembershipTreatment";
                          const membershipName = isMemberBenefit
                            ? getMembershipName(
                                treatment.membershipRewardGroupIds
                              )
                            : null;
                          const isShowingQuantity =
                            treatmentShowingQuantity === treatment.packageId;
                          const isShowingTreatmentSelector =
                            treatmentShowingSelector === treatment.packageId;
                          const itemModel = selectedTreatmentId
                            ? treatment.platformUserPackageItemModels.find(
                                (item) => item.productId === selectedTreatmentId
                              )
                            : treatment.platformUserPackageItemModels[0];
                          const maxQuantity = itemModel?.remainingQuantity ?? 1;
                          const isRedeeming = redeemMutation.isLoading;
                          const redeemedCount =
                            redeemedToday[treatment.packageId] || 0;

                          const isBookableService =
                            treatment.platformUserPackageItemModels.length ===
                              1 && isPartnerEmrBookingEnabled;
                          const hideRedeemButton =
                            isBookableService || isAdminImpersonation;

                          return (
                            <TreatmentCard
                              key={treatment.packageId}
                              id={treatment.packageId}
                              imgUrl={treatment.imageUrl}
                              type={treatment.type}
                              title={getProductTitle(
                                treatment.productName,
                                treatment.variantName
                              )}
                              remainingTreatmentsCount={
                                treatment.remainingTreatmentAmount
                              }
                              packageUnitType={treatment.packageUnitType}
                              membershipName={membershipName}
                              isMemberBenefit={isMemberBenefit}
                              showQuantitySelector={isShowingQuantity}
                              maxQuantity={maxQuantity}
                              isRedeeming={isRedeeming}
                              redeemedToday={redeemedCount}
                              onConfettiTrigger={triggerConfettiFromCard}
                              showTreatmentSelector={isShowingTreatmentSelector}
                              treatments={treatment.platformUserPackageItemModels.map(
                                (item) => ({
                                  id: item.productId,
                                  name: item.name,
                                  remainingQuantity: item.remainingQuantity,
                                })
                              )}
                              onTreatmentSelect={(selections) =>
                                handleTreatmentSelected(treatment, selections)
                              }
                              onClick={() => {
                                if (!isRedeeming) {
                                  navigate(
                                    `${treatment.packageId}?isFromMembership=${isMemberBenefit}&active=true`
                                  );
                                }
                              }}
                              onRedeem={
                                hideRedeemButton
                                  ? undefined
                                  : (quantity) => {
                                      if (isRedeeming) return;

                                      if (isShowingQuantity) {
                                        // Redeem with selected quantity
                                        handleRedeem(
                                          treatment,
                                          quantity,
                                          itemModel?.productId
                                        );
                                      } else {
                                        // First click - check if we need quantity selector
                                        handleRedeemButtonClick(treatment);
                                      }
                                    }
                              }
                              showRemainingQuantity={!isBookableService}
                              onCancelQuantitySelector={() => {
                                setTreatmentShowingQuantity(null);
                                setTreatmentShowingSelector(null);
                                setSelectedTreatmentId(null);
                              }}
                              onBookAppointment={
                                bookablePlatformLocations.length > 0 &&
                                !isPartnerEmrBookingEnabled &&
                                !isAdminImpersonation
                                  ? (e) => {
                                      e.stopPropagation();
                                      handleBookAppointmentClick();
                                    }
                                  : undefined
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                }
              )}
            </>
          )}

          {/* Past Treatments - Grouped by Location */}
          {pastTreatments.length > 0 && (
            <>
              {Object.entries(pastTreatmentsByLocation).map(
                ([locationId, locationTreatments]) => {
                  const locationName =
                    locationId !== "null"
                      ? locationsIdToNameMap.get(locationId)
                      : null;

                  return (
                    <div key={locationId} className="pb-6">
                      <div className="mb-4 px-4">
                        <h2 className="text-sub2 uppercase text-secondary">
                          Past Treatments
                        </h2>
                        {locationName && (
                          <span className="text-body3 font-medium text-secondary">
                            {locationName}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-3 px-4">
                        {locationTreatments.map((treatment) => {
                          const isMemberBenefit =
                            treatment.type === "MembershipTreatment";
                          const membershipName = isMemberBenefit
                            ? getMembershipName(
                                treatment.membershipRewardGroupIds
                              )
                            : null;

                          return (
                            <PastTreatmentCard
                              key={treatment.packageId}
                              id={treatment.packageId}
                              imgUrl={treatment.imageUrl}
                              type={treatment.type}
                              title={getProductTitle(
                                treatment.productName,
                                treatment.variantName
                              )}
                              remainingTreatmentsCount={
                                treatment.remainingTreatmentAmount
                              }
                              packageUnitType={treatment.packageUnitType}
                              membershipName={membershipName}
                              isMemberBenefit={isMemberBenefit}
                              onClick={() =>
                                navigate(
                                  `${treatment.packageId}?isFromMembership=${isMemberBenefit}&active=false`
                                )
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                }
              )}
            </>
          )}

          {!isPartnerEmrBookingEnabled &&
            bookablePlatformLocations.length > 0 &&
            activeTreatments.length > 0 && (
              <div className="pb-safe mt-4 bg-one p-4">
                <Button
                  size="medium"
                  style="primary"
                  fullWidth
                  onClick={handleBookAppointmentClick}
                >
                  {!isUnitedDerm ? "Book appointment" : "Request appointment"}
                </Button>
              </div>
            )}

          {/* Extra bottom padding to ensure content is scrollable past bottom nav */}
          <div className="h-20" />
        </>
      ) : (
        <>
          {/* Legacy Layout */}
          {myTreatments.length === 0 && (
            <div className="mx-4 overflow-clip rounded-lg">
              <NoTreatmentsContainer text="You haven't purchased any treatments yet. Browse the shop to buy your first treatment and start your journey!" />
            </div>
          )}

          {memberBenefits.length > 0 && (
            <>
              <span className="block pb-6 text-center text-sub2 uppercase text-secondary">
                Member benefits
              </span>
              <div className="px-4 pb-6">
                <TreatmentList
                  // @ts-expect-error TODO: fix this type inconsistency
                  treatments={memberBenefits.map(
                    ({
                      active,
                      packageId,
                      imageUrl,
                      type,
                      productName,
                      variantName,
                      remainingTreatmentAmount,
                      membershipRewardGroupIds,
                      totalTreatmentAmount,
                      packageUnitType,
                    }) => ({
                      id: packageId,
                      imgUrl: imageUrl,
                      type: type,
                      bonus: getBonusText(membershipRewardGroupIds),
                      title: getProductTitle(productName, variantName),
                      active: active,
                      remainingTreatmentsCount: remainingTreatmentAmount,
                      totalTreatments: totalTreatmentAmount,
                      packageUnitType: packageUnitType,
                      onClick: () =>
                        navigate(
                          `${packageId}?isFromMembership=true&active=${active}`
                        ),
                    })
                  )}
                />
              </div>
            </>
          )}

          {!!treatments.length && (
            <>
              <span className="block pb-6 text-center text-sub2 uppercase text-secondary">
                Purchased treatments
              </span>
              {(!isSelectPatientLocationActive ||
                treatmentsWithUnknownLocation) && (
                <div className="px-4 pb-10">
                  <TreatmentList
                    treatments={(isSelectPatientLocationActive
                      ? treatmentsWithUnknownLocation
                      : treatments
                    ).map(
                      ({
                        active,
                        packageId,
                        imageUrl,
                        type,
                        productName,
                        variantName,
                        remainingTreatmentAmount,
                        totalTreatmentAmount,
                        packageUnitType,
                      }) => ({
                        id: packageId,
                        imgUrl: imageUrl,
                        type,
                        title: getProductTitle(productName, variantName),
                        active,
                        remainingTreatmentsCount: remainingTreatmentAmount,
                        totalTreatments: totalTreatmentAmount,
                        packageUnitType,
                        onClick: () =>
                          navigate(
                            `${packageId}?isFromMembership=false&active=${active}`
                          ),
                      })
                    )}
                  />
                </div>
              )}
              <IfFlag flag="selectPatientLocation">
                {Object.entries(treatmentsByLocationId).map(
                  ([locationId, treatments]) => {
                    const locationName = locationsIdToNameMap.get(locationId);

                    if (!locationName) {
                      return null;
                    }

                    return (
                      <div key={locationId}>
                        <span className="mb-2 block px-4 uppercase text-secondary">
                          {locationName}
                        </span>
                        <div className="px-4 pb-10">
                          <TreatmentList
                            treatments={treatments.map(
                              ({
                                active,
                                packageId,
                                imageUrl,
                                type,
                                productName,
                                variantName,
                                remainingTreatmentAmount,
                                totalTreatmentAmount,
                                packageUnitType,
                              }) => ({
                                id: packageId,
                                imgUrl: imageUrl,
                                type,
                                title: getProductTitle(
                                  productName,
                                  variantName
                                ),
                                active,
                                remainingTreatmentsCount:
                                  remainingTreatmentAmount,
                                totalTreatments: totalTreatmentAmount,
                                packageUnitType,
                                onClick: () =>
                                  navigate(
                                    `${packageId}?isFromMembership=false&active=${active}&locationId=${locationId}`
                                  ),
                              })
                            )}
                          />
                        </div>
                      </div>
                    );
                  }
                )}
              </IfFlag>
              {!isPartnerEmrBookingEnabled &&
                bookablePlatformLocations.length > 0 && (
                  <div className="sticky bottom-14 mt-auto bg-one p-4">
                    <Button
                      size="medium"
                      style="primary"
                      fullWidth
                      onClick={handleBookAppointmentClick}
                    >
                      {!isUnitedDerm
                        ? "Book appointment"
                        : "Request appointment"}
                    </Button>
                  </div>
                )}
            </>
          )}
        </>
      )}

      <ModalSelectorList
        size="medium"
        open={isLocationModalOpen}
        title={
          activeTreatmentsCount === 1
            ? "Where will you book this service?"
            : "Where will you book these services?"
        }
        type="location"
        items={bookablePlatformLocations.map(
          ({ id, name, city, state, postalCode, address1 }) => ({
            id,
            title: name ?? "",
            description: `${city}, ${state}, ${postalCode}`,
            subtitle: address1 ?? undefined,
          })
        )}
        checkedItems={[selectedLocationId]}
        onClose={() => {
          setIsLocationModalOpen(false);
        }}
        onFirstButtonClick={() => {
          const selectedLocation = locationMap.get(selectedLocationId);

          const href = selectedLocation
            ? getBookAppointmentAction(
                selectedLocation.bookAppointmentUrl,
                selectedLocation.phoneNumber,
                selectedLocation.email
              )
            : null;
          if (href) {
            Browser.open({ url: href });
          }

          setIsLocationModalOpen(false);
        }}
        firstButtonLabel="Book here"
        firstButtonDisabled={!selectedLocationId}
        onSelect={(locationId) => setSelectedLocationId(locationId)}
      />
    </div>
  );
};

export default WalletTreatmentsPage;
